import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Container } from '@/components/ui/container';
import Categories from '@/components/home/Categories';
import RelatedPurchases from '@/components/home/RelatedPurchases';
import FeaturedListings from '@/components/home/FeaturedListings';
import PremiumListings from '@/components/home/PremiumListings';
import { EnhancedHomeSeoHead } from '@/components/common/EnhancedHomeSeoHead';
import { validateImageUrl } from '@/lib/utils';
import CreateFreeAd from '@/components/home/CreateFreeAd';
import FeaturedStores from '@/components/home/FeaturedStores';
import { SupportChat } from '@/components/support/SupportChat';
import InfoBanner from '@/components/ui/InfoBanner';
import WelcomePopup from '@/components/ui/WelcomePopup';

interface ProfileData {
  username: string | null;
  profile_image: string | null;
}

interface ListingBaseData {
  id: number;
  title: string;
  price: number;
  location: string;
  images: string[] | null;
  is_premium: boolean;
  created_at: string;
  condition: string | null;
  delivery_option: string | null;
  user_id: string | null;
  stock: number | null;
  is_price_negotiable: boolean;
  make: string | null;
  model: string | null;
  year: number | null;
  mileage: number | null;
  transmission: string | null;
  categories: { name: string } | null;
  subcategories: { name: string } | null;
}

interface ListingWithProfile extends ListingBaseData {
  profiles: ProfileData | null;
}

const Index = () => {
  const navigate = useNavigate();
  
  const fetchRecentListings = useCallback(async (page = 0, limit = 10) => {
    const from = page * limit;
    
    const { count, error: countError } = await supabase
      .from('listings')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true)
      .eq('approval_status', 'approved');

    if (countError) {
      console.error("Error fetching listings count:", countError);
      return { data: [], hasMore: false }; 
    }
      
    const { data: listingsData, error: listingsError } = await supabase
      .from('listings')
      .select(`
        id, title, price, location, images, is_premium, created_at, condition,
        delivery_option, user_id, stock, is_price_negotiable, make, model, year,
        mileage, transmission,
        categories (name),
        subcategories (name)
      `)
      .eq('is_active', true)
      .eq('approval_status', 'approved')
      .order('created_at', { ascending: false })
      .range(from, from + limit - 1);

    if (listingsError) {
      console.error("Error fetching recent listings (base):", listingsError);
      return { data: [], hasMore: false };
    }
    
    if (!listingsData) {
      return { data: [], hasMore: false };
    }

    const listingsBase = listingsData as ListingBaseData[];

    const userIds = listingsBase
      .map(listing => listing.user_id)
      .filter((id): id is string => id !== null);

    let profilesMap: Map<string, ProfileData> = new Map();

    if (userIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, profile_image')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching profiles for recent listings:', profilesError);
      } else if (profilesData && Array.isArray(profilesData)) {
        profilesData.forEach(profile => {
          if (profile.id) {
             profilesMap.set(profile.id, {
              username: profile.username || null,
              profile_image: profile.profile_image || null,
            });
          }
        });
      }
    }
    
    const combinedData = listingsBase.map(listing => {
      const profile = listing.user_id ? profilesMap.get(listing.user_id) : null;
      let cleanImages = listing.images;
      if (listing.images && listing.images.length > 0) {
        cleanImages = listing.images.map((img: string) => {
          if (img && (img.includes('supabase.co/storage') || img.includes('object/public'))) {
            return img.split('?')[0];
          }
          return img;
        });
      }
      return { 
        ...listing, 
        images: cleanImages,
        profiles: profile || null 
      } as ListingWithProfile;
    });
    
    const hasMore = count ? from + limit < count : false;
    
    return { 
      data: combinedData,
      hasMore
    };
  }, []);

  return (
    <>
      <EnhancedHomeSeoHead 
        totalListings={0} // You can fetch this from your analytics if needed
      />
      <Navbar />
      <SupportChat />
      <WelcomePopup />
      
      <Container className="pt-16 md:pt-20">
        {/* Info Banner Section */}
        <InfoBanner />

        {/* Categories Section - Main Focus */}
        <Categories />

       {/* Create Free Ad Section */}
        <CreateFreeAd />

        {/* Featured Stores Section - New */}
        <FeaturedStores />

        {/* Premium Listings Section */}
        <PremiumListings />
        
        
        
        {/* Empfehlungen für dich (Recommendations) */}
        <RelatedPurchases
          title="Rekomandime për ty"
          limit={8}
          buttonLink="/search"
          buttonText="Shiko më shumë"
        />
        
        {/* Produktet e Fundit (Latest Products) with infinite scroll */}
        <FeaturedListings
          title="Shpalljet e Fundit"
          fetchListings={fetchRecentListings}
          buttonLink="/search?sort=newest"
          buttonText="Shiko të gjitha"
          infiniteScroll={true}
          itemsPerPage={10}
        />
      </Container>
      <Footer />
    </>
  );
};

export default Index;
